import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react'
import { animate, stagger } from 'animejs'
import { NOVA_FLAVOURS } from '../data/flavours'
import '../styles/artifact-archive.css'

type ArtifactArchiveProps = {
  motionEnabled: boolean
}

const ARCHIVE_NOTES = {
  'deep-space': {
    title: 'The first trace.',
    description: 'A violet nebula, a split lunar mark. The original frequency, captured on a surface that keeps a little of the dark.',
    signature: 'Violet nebula',
  },
  'ancient-spark': {
    title: 'Before the first fire.',
    description: 'Ion blue breaks through the dark. A star held at its brightest moment, with the same unmistakable mark at its centre.',
    signature: 'Ion-blue starfield',
  },
  'event-horizon': {
    title: 'A point of no return.',
    description: 'An orange flare at the edge of absolute black. The signal changes, but the mark survives the crossing.',
    signature: 'Orange accretion field',
  },
} as const

function ArchiveTrace() {
  return (
    <svg className="archive__trace" viewBox="0 0 1440 802" fill="none" aria-hidden="true">
      <path className="archive__trace-grid" d="M120 0v802M240 0v802M360 0v802M480 0v802M600 0v802M720 0v802M840 0v802M960 0v802M1080 0v802M1200 0v802M1320 0v802M0 134h1440M0 268h1440M0 402h1440M0 536h1440M0 670h1440" />
      <path className="archive__trace-rule" d="M526 126h217v494H526zM526 370H148v-93M743 206h320v-66M840 620h269v96M795 538v126M733 601h124" />
      <circle className="archive__trace-orbit" cx="795" cy="601" r="55" />
      <circle className="archive__trace-orbit" cx="795" cy="601" r="81" strokeDasharray="2 10" />
      <circle cx="148" cy="277" r="5" fill="currentColor" />
      <circle cx="1063" cy="140" r="5" fill="currentColor" />
      <path className="archive__trace-cross" d="M48 44h28M62 30v28M1364 44h28M1378 30v28M48 758h28M62 744v28M1364 758h28M1378 744v28" />
      <text x="148" y="252">SPLIT LUNAR GLYPH</text>
      <text x="892" y="118">CHROMATIC FIELD</text>
      <text x="929" y="746">SIGNAL SIGNATURE</text>
    </svg>
  )
}

export function ArtifactArchive({ motionEnabled }: ArtifactArchiveProps) {
  const sectionRef = useRef<HTMLElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([])
  const [activeIndex, setActiveIndex] = useState(0)
  const [scanPosition, setScanPosition] = useState(38)
  const [imageError, setImageError] = useState(false)
  const selected = NOVA_FLAVOURS[activeIndex]
  const note = ARCHIVE_NOTES[selected.id]

  useEffect(() => {
    const section = sectionRef.current
    if (!section) return
    let reveal: ReturnType<typeof animate> | undefined
    let revealed = false
    const canAnimate = motionEnabled && !window.matchMedia('(prefers-reduced-motion: reduce)').matches

    const observer = new IntersectionObserver(([entry]) => {
      section.dataset.inView = entry.isIntersecting ? 'true' : 'false'
      if (!entry.isIntersecting || revealed || !canAnimate) return
      revealed = true
      reveal = animate(section.querySelectorAll('[data-archive-reveal]'), {
        opacity: [0, 1],
        y: [22, 0],
        duration: 850,
        delay: stagger(95),
        ease: 'outQuart',
      })
    }, { threshold: 0.08 })
    observer.observe(section)

    return () => {
      observer.disconnect()
      reveal?.revert()
    }
  }, [motionEnabled])

  useEffect(() => {
    const panel = panelRef.current
    if (!panel || !motionEnabled || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const transition = animate(panel.querySelectorAll('[data-channel-reveal]'), {
      opacity: [0.42, 1],
      y: [6, 0],
      duration: 520,
      delay: stagger(55),
      ease: 'outCubic',
    })
    const scan = animate(panel.querySelectorAll('.archive__scan-line'), {
      opacity: [0.35, 1],
      duration: 720,
      ease: 'outSine',
    })
    return () => {
      transition.revert()
      scan.revert()
    }
  }, [activeIndex, motionEnabled])

  function selectChannel(index: number, focus = false) {
    if (index !== activeIndex) {
      setActiveIndex(index)
      setImageError(false)
    }
    if (focus) tabRefs.current[index]?.focus()
  }

  function handleTabKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    let nextIndex: number
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % NOVA_FLAVOURS.length
    else if (event.key === 'ArrowLeft') nextIndex = (index - 1 + NOVA_FLAVOURS.length) % NOVA_FLAVOURS.length
    else if (event.key === 'Home') nextIndex = 0
    else if (event.key === 'End') nextIndex = NOVA_FLAVOURS.length - 1
    else return
    event.preventDefault()
    selectChannel(nextIndex, true)
  }

  const archiveStyle = {
    '--archive-accent': selected.accent,
    '--archive-bright': selected.accentBright,
    '--archive-shadow': selected.shadow,
    '--archive-field': selected.field,
    '--archive-scan': `${scanPosition}%`,
  } as CSSProperties

  return (
    <section
      className="archive"
      id="artifact"
      aria-labelledby="artifact-title"
      ref={sectionRef}
      style={archiveStyle}
      data-motion={motionEnabled ? 'on' : 'off'}
      data-in-view="false"
    >
      <div className="archive__header" data-archive-reveal>
        <p><span className="archive__status-dot" />NOVA ARCHIVE / VISUAL IDENTITY</p>
        <p>THREE FREQUENCIES. ONE MARK.</p>
      </div>

      <div className="archive__intro">
        <h2 id="artifact-title" data-archive-reveal>
          Every mark<br />
          <span>leaves a trace.</span>
        </h2>
        <p className="archive__intro-copy" data-archive-reveal>
          The sky left its signature.<br />{' '}
          We wrapped it around a can.
        </p>
      </div>

      <div className="archive__channels" role="tablist" aria-label="Choose a label to explore" data-archive-reveal>
        {NOVA_FLAVOURS.map((flavour, index) => (
          <button
            className="archive__channel"
            key={flavour.id}
            id={`archive-tab-${flavour.id}`}
            type="button"
            role="tab"
            aria-selected={index === activeIndex}
            aria-controls="archive-panel"
            tabIndex={index === activeIndex ? 0 : -1}
            ref={(node) => { tabRefs.current[index] = node }}
            onClick={() => selectChannel(index)}
            onKeyDown={(event) => handleTabKeyDown(event, index)}
            style={{ '--channel-accent': flavour.accentBright } as CSSProperties}
          >
            <span className="archive__channel-code"><i />{flavour.code}</span>
            <span className="archive__channel-name">{flavour.name}</span>
            <span className="archive__channel-arrow" aria-hidden="true">↗</span>
          </button>
        ))}
      </div>

      <div
        ref={panelRef}
        className="archive__panel"
        id="archive-panel"
        role="tabpanel"
        aria-labelledby={`archive-tab-${selected.id}`}
        tabIndex={0}
        data-archive-reveal
      >
        <div className="archive__plate-header">
          <span>FLAT LABEL / {selected.code}</span>
          <span className="archive__plate-status"><i />SIGNAL RECOVERED</span>
        </div>

        <div className={`archive__artwork${imageError ? ' archive__artwork--missing' : ''}`} data-channel-reveal>
          {imageError ? (
            <p className="archive__image-fallback">{selected.name}<span>Artwork unavailable. Try another frequency.</span></p>
          ) : (
            <img
              key={selected.id}
              className="archive__label"
              src={`${import.meta.env.BASE_URL}media/nova-label-${selected.id}.webp`}
              alt={`Original Nova Energy ${selected.name} label, featuring the split lunar-white Nova mark and ${note.signature.toLowerCase()}.`}
              width="1440"
              height="802"
              loading="lazy"
              decoding="async"
              onError={() => setImageError(true)}
            />
          )}
          <div className="archive__scan-overlay" aria-hidden="true">
            <ArchiveTrace />
          </div>
          <div className="archive__scan-line" aria-hidden="true">
            <span className="archive__scan-marker">↔</span>
          </div>
          <span className="archive__corner archive__corner--tl" aria-hidden="true" />
          <span className="archive__corner archive__corner--tr" aria-hidden="true" />
          <span className="archive__corner archive__corner--bl" aria-hidden="true" />
          <span className="archive__corner archive__corner--br" aria-hidden="true" />
          <label className="sr-only" htmlFor="archive-image-scan">Drag to reveal the label analysis</label>
          <input
            className="archive__image-range"
            id="archive-image-scan"
            type="range"
            min="0"
            max="100"
            value={scanPosition}
            aria-valuetext={`${scanPosition}% analysis overlay`}
            aria-describedby="archive-scan-hint"
            onChange={(event) => setScanPosition(Number(event.currentTarget.value))}
          />
        </div>

        <div className="archive__scan-controls">
          <p id="archive-scan-hint"><span aria-hidden="true">↔</span>Drag across the label to inspect its signature</p>
          <div className="archive__scan-actions" role="group" aria-label="Label inspection view">
            <button type="button" onClick={() => setScanPosition(0)} aria-pressed={scanPosition === 0}>Artwork</button>
            <button type="button" onClick={() => setScanPosition(100)} aria-pressed={scanPosition === 100}>Analysis</button>
          </div>
        </div>

        <div className="archive__record" data-channel-reveal>
          <div className="archive__record-copy">
            <p className="archive__record-eyebrow">{selected.code} / {selected.name.toUpperCase()}</p>
            <h3>{note.title}</h3>
            <p>{note.description}</p>
          </div>
          <dl className="archive__metadata">
            <div><dt>Frequency</dt><dd>{selected.frequency}</dd></div>
            <div><dt>Signature</dt><dd>{note.signature}</dd></div>
            <div><dt>Mark</dt><dd>Split lunar glyph</dd></div>
          </dl>
          <div className="archive__palette">
            <span className="archive__palette-label">CHROMATIC DNA</span>
            <div className="archive__swatches" aria-label={`${selected.name} colour palette`}>
              {[selected.shadow, selected.accent, selected.accentBright, '#f7f4ff'].map((colour) => (
                <span key={colour} className="archive__swatch" style={{ backgroundColor: colour }} title={colour}>
                  <span className="sr-only">{colour}</span>
                </span>
              ))}
            </div>
            <span className="archive__palette-caption">DARK MATTER → LUNAR WHITE</span>
          </div>
        </div>
      </div>
      <div className="archive__footer" aria-hidden="true"><span>END OF SURFACE SCAN</span><span /><span>NOVA ENERGY / EST. BEYOND EARTH</span></div>
    </section>
  )
}
